import { useEffect, useState } from 'react';

import { getMyProfile, saveMyProfile, type ArtistProfile as Profile, type ProfileInput } from '../../lib/artist';
import { ApiError } from '../../lib/apiClient';
import { formatDate } from '../../lib/format';
import './ArtistPages.css';

/**
 * Own profile — create on first visit, edit thereafter.
 *
 * `approved` and `tier` are shown but not editable, and the engine does not
 * accept them from this endpoint either: an artist who could approve themselves
 * could be booked, because createAgreement is the thing that refuses unapproved
 * artists. Rendering them read-only matches where the decision actually sits.
 */

interface FormState {
  stageName: string;
  bio: string;
  genre: string;
  primaryMarket: string;
  profileImageUrl: string;
  coverImageUrl: string;
  instagramHandle: string;
  instagramFollowers: string;
  tiktokHandle: string;
  tiktokFollowers: string;
  spotifyMonthlyListeners: string;
  youtubeSubscribers: string;
}

const EMPTY: FormState = {
  stageName: '', bio: '', genre: '', primaryMarket: '',
  profileImageUrl: '', coverImageUrl: '',
  instagramHandle: '', instagramFollowers: '',
  tiktokHandle: '', tiktokFollowers: '',
  spotifyMonthlyListeners: '', youtubeSubscribers: '',
};

const num = (v: string) => (v.trim() === '' ? null : Number(v));

function toInput(f: FormState): ProfileInput {
  return {
    stageName: f.stageName.trim(),
    bio: f.bio.trim() || null,
    genre: f.genre.trim() || null,
    primaryMarket: f.primaryMarket.trim() || null,
    profileImageUrl: f.profileImageUrl.trim() || null,
    coverImageUrl: f.coverImageUrl.trim() || null,
    instagramHandle: f.instagramHandle.trim() || null,
    instagramFollowers: num(f.instagramFollowers),
    tiktokHandle: f.tiktokHandle.trim() || null,
    tiktokFollowers: num(f.tiktokFollowers),
    spotifyMonthlyListeners: num(f.spotifyMonthlyListeners),
    youtubeSubscribers: num(f.youtubeSubscribers),
  };
}

export function ArtistProfile() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyProfile()
      .then((p) => {
        if (cancelled) return;
        setProfile(p);
        if (p) {
          setForm({
            stageName: p.stageName ?? '',
            bio: p.bio ?? '',
            genre: p.genre ?? '',
            primaryMarket: p.primaryMarket ?? '',
            profileImageUrl: p.profileImageUrl ?? '',
            coverImageUrl: p.coverImageUrl ?? '',
            instagramHandle: p.social.instagramHandle ?? '',
            instagramFollowers: p.social.instagramFollowers?.toString() ?? '',
            tiktokHandle: p.social.tiktokHandle ?? '',
            tiktokFollowers: p.social.tiktokFollowers?.toString() ?? '',
            spotifyMonthlyListeners: p.social.spotifyMonthlyListeners?.toString() ?? '',
            youtubeSubscribers: p.social.youtubeSubscribers?.toString() ?? '',
          });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load your profile.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  async function save() {
    setBusy(true);
    setError(null);
    setFlash(null);
    try {
      const saved = await saveMyProfile(toInput(form));
      setProfile(saved);
      setFlash(profile ? 'Profile saved.' : 'Profile created. A Voleto admin will review it for approval.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save your profile.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="art-status">Loading…</p>;

  const creating = !profile;

  return (
    <div className="art-page art-page--narrow">
      <header className="art-header">
        <div>
          <h1 className="art-title">{creating ? 'Create your profile' : 'Profile'}</h1>
          <p className="art-lede">
            This is what organizers see when they search for artists to book.
          </p>
        </div>
        {profile && (
          <span className={`art-badge art-badge--${profile.approved ? 'signed' : 'pending'}`}>
            {profile.approved ? 'Approved' : 'Awaiting approval'}
          </span>
        )}
      </header>

      {error && <p className="art-error" role="alert">{error}</p>}
      {flash && <p className="art-flash" role="status">{flash}</p>}

      {profile && !profile.approved && (
        <p className="art-flash" role="status">
          A Voleto admin reviews every artist profile. Until yours is approved, organizers
          cannot send you booking requests, approval is not something you can set here.
        </p>
      )}

      <section className="art-card">
        <div className="art-form">
          <label className="field art-form__wide">
            <span className="field__label">Stage name *</span>
            <input className="field__input" type="text" value={form.stageName} disabled={busy}
              onChange={(e) => set({ stageName: e.target.value })} />
          </label>

          <label className="field art-form__wide">
            <span className="field__label">Bio</span>
            <textarea className="field__input" rows={4} value={form.bio} disabled={busy}
              onChange={(e) => set({ bio: e.target.value })}
              placeholder="Who you are and what a promoter is booking." />
          </label>

          <label className="field">
            <span className="field__label">Genre</span>
            <input className="field__input" type="text" value={form.genre} disabled={busy}
              onChange={(e) => set({ genre: e.target.value })} placeholder="indie" />
          </label>

          <label className="field">
            <span className="field__label">Primary market</span>
            <input className="field__input" type="text" value={form.primaryMarket} disabled={busy}
              onChange={(e) => set({ primaryMarket: e.target.value })} placeholder="Denver" />
          </label>

          <label className="field art-form__wide">
            <span className="field__label">Profile image URL</span>
            <input className="field__input mono" type="url" value={form.profileImageUrl} disabled={busy}
              onChange={(e) => set({ profileImageUrl: e.target.value })} />
          </label>

          <label className="field art-form__wide">
            <span className="field__label">Cover image URL</span>
            <input className="field__input mono" type="url" value={form.coverImageUrl} disabled={busy}
              onChange={(e) => set({ coverImageUrl: e.target.value })} />
          </label>
        </div>
      </section>

      <section className="art-card">
        <h2 className="art-section__title">Audience</h2>
        <p className="art-section__note">
          Organizers weigh these when deciding whether your draw fits their room.
        </p>
        <div className="art-form">
          <label className="field">
            <span className="field__label">Instagram handle</span>
            <input className="field__input" type="text" value={form.instagramHandle} disabled={busy}
              onChange={(e) => set({ instagramHandle: e.target.value })} placeholder="@name" />
          </label>
          <label className="field">
            <span className="field__label">Instagram followers</span>
            <input className="field__input mono" type="number" min="0" step="1"
              value={form.instagramFollowers} disabled={busy}
              onChange={(e) => set({ instagramFollowers: e.target.value })} />
          </label>
          <label className="field">
            <span className="field__label">TikTok handle</span>
            <input className="field__input" type="text" value={form.tiktokHandle} disabled={busy}
              onChange={(e) => set({ tiktokHandle: e.target.value })} placeholder="@name" />
          </label>
          <label className="field">
            <span className="field__label">TikTok followers</span>
            <input className="field__input mono" type="number" min="0" step="1"
              value={form.tiktokFollowers} disabled={busy}
              onChange={(e) => set({ tiktokFollowers: e.target.value })} />
          </label>
          <label className="field">
            <span className="field__label">Spotify monthly listeners</span>
            <input className="field__input mono" type="number" min="0" step="1"
              value={form.spotifyMonthlyListeners} disabled={busy}
              onChange={(e) => set({ spotifyMonthlyListeners: e.target.value })} />
          </label>
          <label className="field">
            <span className="field__label">YouTube subscribers</span>
            <input className="field__input mono" type="number" min="0" step="1"
              value={form.youtubeSubscribers} disabled={busy}
              onChange={(e) => set({ youtubeSubscribers: e.target.value })} />
          </label>
        </div>
      </section>

      {profile && (
        <dl className="art-facts">
          <div><dt>Tier</dt><dd>{profile.tier}</dd></div>
          <div>
            <dt>Approved</dt>
            <dd>{profile.approvedAt ? formatDate(profile.approvedAt) : <span className="art-muted">Not yet</span>}</dd>
          </div>
          <div><dt>Created</dt><dd>{formatDate(profile.createdAt)}</dd></div>
        </dl>
      )}

      <div className="art-actions">
        <button type="button" className="button button--primary"
          disabled={busy || !form.stageName.trim()} onClick={save}>
          {busy ? 'Saving…' : creating ? 'Create profile' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
